import { aiMayReadResponses, RESPONSE_BODY_WITHHELD_NOTE } from '../../services/ai/responseAccess';
import { redactBody } from '../../utils/redactResponse';
import { describeToolCall } from './toolPresentation';
import { condenseTerminalResult, condenseDocumentResult, clampOutput } from '../../services/ai/outputBudget';
import { projectUnlistedResult } from '../../services/ai/resultProjection';
import { condenseFolderBatchResult } from '../../services/ai/batch/folderBatch';
import { condenseWebSearchResult } from '../../services/ai/webSearch';
import { condenseFetchUrlResult } from '../../services/ai/fetchUrl';
import { scrubForModel, scrubTextForModel } from '../../services/ai/modelBoundary';
import { scrubDbErrorValues } from '../../services/ai/dbErrorScrub';

/** Slim payload sent back to Claude for lookup tools (saves tokens). */
/** Caps for get_board — see the case below. */
export const BOARD_TASK_LIMIT = 60;

export const BOARD_DESCRIPTION_LIMIT = 400;

export function condenseToolResultForApi(toolName, rawResult, input) {
  // The single place every tool result passes on its way to the provider:
  // image keys and credential shapes go here, before any case below reads a
  // field (services/ai/modelBoundary.js — the in-app twin of MCP's runHandler).
  const result = scrubForModel(rawResult);
  // Errors bypass the whole switch below, so this line is the ONLY minimisation
  // an error message ever gets — and an exception is the least curated string in
  // the app. A driver that cannot connect reports the connection string it
  // tried, password included; an HTTP client echoes the Authorization header it
  // sent. Scrub before it becomes prompt text for a third-party provider.
  // A DB error can quote a row value (MySQL 1062) - services/ai/dbErrorScrub.js.
  if (result?.error) {
    const msg = String(result.error);
    return JSON.stringify({ error: toolName === 'db_run_sql' ? scrubDbErrorValues(msg, input?.sql) : msg });
  }

  switch (toolName) {
    case 'send_request':
      return JSON.stringify({
        status: result.status,
        status_text: result.status_text,
        time_ms: result.time_ms,
        content_type: result.content_type,
        note: 'Full response body is shown in the UI panel',
      });
    case 'get_request':
      if (result.request) {
        const r = result.request;
        return JSON.stringify({
          collection_id: result.collection_id,
          // type / auth / protocol: services/ai/requestFacts.js — names and
          // types only, never a credential value.
          request: { id: r.id, name: r.name, method: r.method, url: r.url, ...(result.facts || {}) },
        });
      }
      return JSON.stringify(result);
    case 'create_collection':
      return JSON.stringify(result.collection || result);
    case 'create_folders':
      // ids, names, flags and per-item errors — see folderBatch.js.
      return JSON.stringify(condenseFolderBatchResult(result));
    case 'create_request':
      return JSON.stringify(result.request || result);
    // ── Web discovery ────────────────────────────────────────────────────
    // Both carry free text on purpose (a snippet, a page's text), which the
    // default projector would drop — and a search without snippets or a page
    // without its text is useless to the model. fetch_url's text is clamped
    // and scrubbed at the source; the condense re-clamps so a later change to
    // the executor cannot widen what crosses.
    case 'web_search':
      return JSON.stringify(condenseWebSearchResult(result));
    case 'fetch_url':
      return JSON.stringify(condenseFetchUrlResult(result));
    case 'update_request':
      return JSON.stringify(
        result.error
          ? { error: result.error }
          : { success: true, request_id: result.request_id, updated_fields: result.updated_fields }
      );
    case 'move_requests':
      return JSON.stringify({
        moved_count: result.moved_count,
        failed_count: result.failed_count,
        moved: (result.moved || []).map((m) => (
          m.error
            ? { request_id: m.request_id, error: m.error }
            : { request_id: m.request_id, request_name: m.request_name, folder_id: m.folder_id, folder_name: m.folder_name }
        )),
      });
    case 'run_collection': {
      const mayReadBodies = aiMayReadResponses();
      // P2: give the model enough to DIAGNOSE failures — failing test names/errors
      // + a truncated body snippet, but only for failed requests (first 5), and
      // slim rows for the passing ones so token cost stays bounded.
      const failures = (result.results || [])
        .filter((r) => !r.passed)
        .slice(0, 5)
        .map((r) => ({
          request_id: r.request_id,
          name: r.name,
          method: r.method,
          url: r.url,
          status: r.status,
          error: r.error,
          // An assertion message embeds the ACTUAL value it compared, i.e. body
          // data — so with the privacy switch off only the test name crosses.
          failed_tests: (r.tests || []).filter((t) => !t.passed).map((t) => (
            mayReadBodies ? { name: t.name, error: t.error } : { name: t.name }
          )),
          // Privacy gate: a failure's body snippet crosses only when the user
          // opted in, and is redacted even then (at source, and again here).
          ...(mayReadBodies
            ? { response_body_snippet: r.response_body_snippet == null ? r.response_body_snippet : redactBody(String(r.response_body_snippet)).body }
            : {}),
        }));
      return JSON.stringify({
        collection_name: result.collection_name,
        total: result.total,
        passed_count: result.passed_count,
        failed_count: result.failed_count,
        total_time_ms: result.total_time_ms,
        results: (result.results || []).map((r) => ({
          name: r.name,
          method: r.method,
          status: r.status,
          passed: r.passed,
          error: r.error,
        })),
        failures,
        ...(mayReadBodies ? {} : { response_bodies_withheld: RESPONSE_BODY_WITHHELD_NOTE }),
      });
    }
    // ── Extension tools ──────────────────────────────────────────────────
    case 'create_board':
      return JSON.stringify({ board_id: result.board_id, list_ids: result.list_ids });
    case 'create_list':
      return JSON.stringify({ list_id: result.list_id });
    case 'create_task':
      return JSON.stringify({ task_id: result.task_id });
    case 'create_document':
    case 'create_doc_folder':
      return JSON.stringify({ id: result.document_id || result.folder_id });
    // Bounded, not withheld: the model needs the output to diagnose, but the
    // raw default shipped an entire server log / document to the provider.
    // See services/ai/outputBudget.js.
    case 'terminal_run_command':
      return JSON.stringify(condenseTerminalResult(result));
    case 'read_document':
      return JSON.stringify(condenseDocumentResult(result));
    case 'db_run_sql':
      // NEVER feed rows back — only execution metadata.
      return JSON.stringify({
        executed: result.executed,
        row_count: result.row_count,
        columns: result.columns,
      });
    case 'create_mock_example':
      // Never the body. For source:'last_response' that body is a live
      // production payload the user never chose to show anyone; the whole
      // point of copying it in-process is that it does not pass through the
      // model. A byte count is enough for the model to confirm what it did.
      return JSON.stringify({
        success: result.success,
        request_id: result.request_id,
        example_id: result.example_id,
        name: result.name,
        status: result.status,
        content_type: result.content_type,
        body_bytes: result.body_bytes,
        route_path: result.route_path,
        mock_url: result.mock_url,
        mock_server_running: result.mock_server_running,
        example_count: result.example_count,
        random_between: result.random_between,
      });
    case 'generate_code':
      // The snippet crosses to the model on purpose: "now make it async" or
      // "why does this 401?" are follow-ups the model cannot answer about code
      // it has not seen. What makes that acceptable is that credentials were
      // already replaced with <SECRET_REDACTED> before the string existed —
      // see guardCodegenVariables in useAiWorkspaceActions.js. `code` is the
      // ONLY free text here; everything else is an identifier or a flag.
      return JSON.stringify({
        language: result.language,
        library: result.library,
        method: result.method,
        request_name: result.request_name,
        secrets_redacted: result.secrets_redacted,
        // Bounded like terminal_run_command and read_document. A snippet
        // embeds the request BODY verbatim, and nothing upstream caps that —
        // a 2 MB payload becomes a 2 MB prompt billed to the user's own key.
        code: clampOutput(result.code),
      });
    case 'get_last_response':
      // The one tool that returns a body so the model can write tests — only
      // when the user opted in ("Let AI read responses"), and redacted at the
      // source (useAiActions aiGetLastResponse → getResponseBodyForAi).
      if (result.body_withheld || !aiMayReadResponses()) {
        return JSON.stringify({
          status: result.status,
          status_text: result.status_text,
          content_type: result.content_type,
          body_withheld: true,
          note: RESPONSE_BODY_WITHHELD_NOTE,
        });
      }
      return JSON.stringify({
        status: result.status,
        status_text: result.status_text,
        content_type: result.content_type,
        body: result.body,
        body_truncated: result.body_truncated,
        redaction_count: result.redaction_count,
      });
    case 'generate_tests':
      // The full enumeration of what crosses back, and why each field is safe:
      //
      //   ran / passed_count / failed_count — booleans and counts.
      //   run_error                         — scrubbed in aiGenerateTests.
      //   tests[].name, tests[].error       — script-chosen strings; scrubbed
      //                                       in aiGenerateTests via
      //                                       redactTestRunResults.
      //
      // Deliberately NOT forwarded: `test_script` and `previous_test_script`.
      // The model wrote the first and the second is the user's; neither is
      // scrubbed, so adding either here would leak whatever the user's own
      // script has inlined. Never JSON.stringify(result) in this case, and
      // never add a field the hook has not scrubbed.
      //
      // The scrubbing itself is a substring filter, not containment — a script
      // that transforms a value before throwing it defeats it. What actually
      // contains a NO-GRANT secret is that its value is not in the sandbox at
      // all (withheldKeys, applied at the runScript call site). A
      // `scriptAccess: true` secret IS in the sandbox — generate_tests runs for
      // real, so a signature test has to work — and can be encoded out. Same
      // residual risk the Send path carries.
      //
      // Response-privacy gate (review B1, 13.09.2026): the tests run against the
      // stored response BODY, and a script can put that body into a test name
      // (`pm.test(JSON.stringify(pm.response.json()), …)`) or an assertion can
      // print a real value into `error` — and even bare pass/fail flags are a
      // one-bit-per-test read of the body (review R1). With "Let AI read
      // responses" off the hook does not run the script at all, and nothing
      // about an outcome crosses here either.
      if (!aiMayReadResponses() || result.run_withheld) {
        return JSON.stringify({
          written_to_tests_tab: true,
          ran: false,
          note: `${RESPONSE_BODY_WITHHELD_NOTE} The script was written to the Tests tab but not run; the user can run it with Send.`,
        });
      }
      return JSON.stringify({
        ran: result.ran_against_stored_response,
        passed_count: result.passed_count,
        failed_count: result.failed_count,
        run_error: result.run_error,
        tests: (result.tests || []).map((t) => ({ name: t.name, passed: t.passed, error: t.error })),
      });
    case 'generate_pre_request_script':
      // Only the dry-run summary crosses back to the server. Every field below
      // comes out of `summarizePreRequestDryRun`, which is where the secret
      // policy is applied — variable names, pm.test labels and error strings
      // are all scrubbed there, and no secret value was in the sandbox to begin
      // with (see aiPreRequestDryRun.js). The scripts themselves stay
      // client-side: the model wrote the new one and the previous one is the
      // user's, not ours to ship. Never JSON.stringify(result) here, and never
      // read a field off `result.dry_run` that the summarizer did not scrub.
      return JSON.stringify({
        ran: true,
        applied_to_environment: false,
        dry_run_ok: result.dry_run?.ok ?? false,
        dry_run_error: result.dry_run?.error ?? null,
        // A pm.test that threw is a failure the dry run must not hide behind
        // ok:true — the sandbox files those under `tests`, not `errors`.
        failed_tests: result.dry_run?.failed_tests || [],
        would_set_environment_variables: result.dry_run?.would_set_environment_variables || [],
        would_unset_environment_variables: result.dry_run?.would_unset_environment_variables || [],
        would_set_local_variables: result.dry_run?.would_set_local_variables || [],
      });
    // A board can carry hundreds of tasks, each with a 2,000-character
    // description written by a person — the single largest volume of free
    // text any read tool returns, and uncapped it both blows the context
    // window and ships far more than the model needs to answer.
    case 'get_board': {
      if (!result?.board) return JSON.stringify(result);
      const { board } = result;
      const tasks = board.tasks || [];
      return JSON.stringify({
        board: {
          id: board.id,
          name: board.name,
          lists: board.lists,
          task_count: tasks.length,
          tasks: tasks.slice(0, BOARD_TASK_LIMIT).map((t) => ({
            id: t.id,
            group_id: t.group_id,
            title: t.title,
            // Enough to know what a task is about; not the whole write-up.
            description: (t.description || '').slice(0, BOARD_DESCRIPTION_LIMIT),
            priority: t.priority,
            status: t.status,
            due_date: t.due_date,
            done: t.done,
          })),
          // Announced, so the model asks for a narrower view instead of
          // concluding the board only has this many tasks.
          tasks_omitted: Math.max(0, tasks.length - BOARD_TASK_LIMIT) || undefined,
        },
      });
    }

    // ── Reviewed: the full result is workspace STRUCTURE ─────────────────
    // Each of these was checked individually. They return the inventory the
    // model needs to act — collection and request names, URLs, board and
    // document titles, schema shape, connection labels — and no row data,
    // document body or command output. The variable tools are here because
    // their secret values are already replaced with `<SECRET>` at the source
    // (see the maskSecrets calls in executeTool), which is a stronger guard
    // than anything this function could apply afterwards.
    //
    // Passing them through is a DECISION, not a fallthrough. That is the whole
    // point of the change below: reviewed tools opt in by name.
    //
    // 04.10.2026: get_git_status / ftp_list_connections / ftp_list_dir fell
    // to the default projector, which dropped every field the model needed
    // (connected / ahead / behind, is_dir / size, protocol) — "is my work
    // synced?" was answered from nothing. Reviewed: counts, flags, names and
    // the repo the user chose; never a URL, a host or a file body.
    case 'get_git_status':
    case 'ftp_list_connections':
    case 'ftp_list_dir':
    case 'import_spec':
    case 'get_history':
    case 'create_environment':
    case 'duplicate_environment':
    case 'switch_environment':
    case 'set_auth':
    case 'get_collections':
    case 'get_current_request':
    case 'get_environments':
    case 'get_workspace_variables':
    case 'get_global_variables':
    case 'set_variable':
    case 'get_boards':
    case 'get_documents':
    case 'db_list_connections':
    case 'db_get_schema':
    case 'terminal_list_connections':
    case 'open_board':
    case 'open_document':
    case 'rename_board':
    case 'rename_document':
    case 'delete_board':
    case 'delete_document':
    case 'update_task':
    case 'write_task_description':
      return JSON.stringify(result);

    // ── The default is a PROJECTION, not a passthrough ───────────────────
    // This function is the app's data-minimisation boundary: the last thing
    // that runs before a tool result becomes prompt text sent to a third-party
    // provider. It used to fall through to `JSON.stringify(result)`, so the
    // safe behaviour was opt-in and every gap was one forgotten `case` —
    // that is how terminal_run_command came to ship whole server logs.
    //
    // Now an unlisted tool is projected through a conservative allowlist
    // instead of dumped. Forgetting a tool degrades what the model can see;
    // it can no longer leak the whole result object.
    default:
      return JSON.stringify(projectUnlistedResult(result));
  }
}

/** Compact history entry after terminal tools — avoids feeding full results back to Claude. */
export function buildToolTurnHistoryEntry(textContent, toolCalls) {
  const lines = (toolCalls || []).map((tc) => {
    if (tc.name === 'send_request') {
      const label = tc.input?.use_current_tab
        ? 'active tab'
        : `${tc.input?.method || 'GET'} ${tc.input?.url || ''}`;
      if (tc.result?.error) return `- send_request ${label} → error`;
      const status = [tc.result?.status, tc.result?.status_text].filter(Boolean).join(' ');
      return `- send_request ${label} → ${status || 'ok'}`;
    }
    if (tc.name === 'run_collection') {
      const name = tc.result?.collection_name || tc.input?.collection_name || '';
      if (tc.result?.error) return `- run_collection ${name} → error`;
      return `- run_collection ${name} → ${tc.result?.passed_count ?? 0}/${tc.result?.total ?? 0} passed`;
    }
    if (tc.name === 'create_collection') {
      const name = tc.result?.collection?.name || tc.input?.name || '';
      return tc.result?.error ? `- create_collection ${name} → error` : `- create_collection ${name} → created`;
    }
    if (tc.name === 'create_folders') {
      const count = tc.input?.folders?.length || 0;
      if (tc.result?.error) return `- create_folders ${count} folder(s) → error`;
      return `- create_folders ${count} folder(s) → created ${tc.result?.created_count ?? 0}, failed ${tc.result?.failed_count ?? 0}`;
    }
    if (tc.name === 'create_request') {
      const name = tc.result?.request?.name || tc.input?.name || '';
      return tc.result?.error ? `- create_request ${name} → error` : `- create_request ${name} → created`;
    }
    if (tc.name === 'db_run_sql') {
      if (tc.result?.error) return `- db_run_sql → error`;
      const cols = (tc.result?.columns || []).map((c) => c.name).join(', ');
      return `- db_run_sql → ${tc.result?.row_count ?? 0} row(s)${cols ? ` [${cols}]` : ''} (shown in grid)`;
    }
    if (tc.name === 'write_document') {
      return tc.result?.error ? `- write_document → error` : `- write_document → saved`;
    }
    // Fallback. This matters more than it looks: for a TERMINAL tool the loop
    // stops here, so this line is the model's only record of what happened —
    // and a bare "set_variable: done" hides both which variable and whether it
    // worked. Reuse the card labels rather than grow a second if-chain.
    const label = describeToolCall(tc.name, tc.input);
    // Same scrub as condenseToolResultForApi: this string is appended to
    // anthropicHistoryRef and replayed to the provider on EVERY subsequent turn,
    // so an unscrubbed error here outlives the turn that produced it.
    if (tc.result?.error) {
      return `- ${tc.name} ${label} → error: ${scrubTextForModel(String(tc.result.error)).slice(0, 120)}`;
    }
    if (tc.name === 'terminal_run_command') {
      const code = tc.result?.exit_code;
      return `- terminal_run_command ${label} → exit ${code ?? '?'}${code === 0 ? '' : ' (see panel)'}`;
    }
    return `- ${tc.name} ${label} → done`;
  });
  const prefix = (textContent || '').trim();
  if (lines.length === 0) return prefix || 'Ran the requested tools.';
  const note = '[Tools executed — see panel for full results]';
  return prefix ? `${prefix}\n\n${note}\n${lines.join('\n')}` : `${note}\n${lines.join('\n')}`;
}
